(function () {
  'use strict';

  /* ════════════════════════════════════════════════════════
     Config + tier data — single source of truth, drives both
     card and strip renders.
  ════════════════════════════════════════════════════════ */
  /* Settings are rendered into the page by
     sections/paint-bundle-widget-responsive.liquid; everything below is static
     logic, so it lives in this cacheable asset instead of inline on every page. */
  const SETTINGS = window.GGPaintBundleConfig;
  if (!SETTINGS || !SETTINGS.collectionHandle) return;

  const CONFIG = {
    collectionHandle: SETTINGS.collectionHandle,
    tiers: SETTINGS.tiers,
    itemNoun: SETTINGS.itemNoun,
    sectionType: SETTINGS.sectionType,
    headerOffset: SETTINGS.headerOffset === true,
    headerSelector: SETTINGS.headerSelector,
    collapsedKey: 'gg_paint_widget_collapsed',
    expandedKey:  'gg_paint_strip_expanded',
  };

  // Liquid counted the qualifying units for the cart this page was rendered
  // with, so the first render needs no network at all.
  let paintQty = Number(SETTINGS.paintQty) || 0;

  // Left over from the version that cached a downloaded product-id list.
  try { localStorage.removeItem('gg_paint_widget_ids_v1'); } catch (_) {}

  /* ── DOM refs — null if that layout isn't rendered ───────── */
  const cardEl   = document.getElementById('gg-paint-widget');
  const stripEl  = document.getElementById('gg-paint-strip');

  // Card-only refs
  const cardToggle    = document.getElementById('gg-widget-toggle');
  const cardBanner    = document.getElementById('gg-tier-banner');
  const cardBannerTx  = document.getElementById('gg-tier-banner-text');
  const cardMsg       = document.getElementById('gg-progress-msg');
  const cardBarFill   = document.getElementById('gg-bar-fill');
  const cardBarTrack  = document.getElementById('gg-bar-track');
  const cardPips      = document.getElementById('gg-tier-pips');
  const cardChip      = document.getElementById('gg-footer-chip');
  const cardTierBadge = document.getElementById('gg-tier-badge');

  // Strip-only refs
  const stripToggle = document.getElementById('gg-strip-toggle');
  const stripCount  = document.getElementById('gg-strip-count');
  const stripStatus = document.getElementById('gg-strip-status');
  const stripFill   = document.getElementById('gg-strip-fill');
  const stripPips   = document.getElementById('gg-strip-pips');
  const stripDetail = document.getElementById('gg-strip-detail');

  let lastPaintQty    = -1;
  let lastAppliedCode = null;
  let lastActiveTier  = null;

  /* ── Persisted UI state ──────────────────────────────────── */
  const isCardCollapsed = () => sessionStorage.getItem(CONFIG.collapsedKey) === '1';
  const isStripExpanded = () => sessionStorage.getItem(CONFIG.expandedKey) === '1';

  if (cardToggle) cardToggle.addEventListener('click', () => {
    const next = !isCardCollapsed();
    sessionStorage.setItem(CONFIG.collapsedKey, next ? '1' : '0');
    cardEl.classList.toggle('gg-collapsed', next);
    cardToggle.setAttribute('aria-expanded', next ? 'false' : 'true');
  });
  if (stripToggle) stripToggle.addEventListener('click', () => {
    const next = !isStripExpanded();
    sessionStorage.setItem(CONFIG.expandedKey, next ? '1' : '0');
    stripEl.classList.toggle('gg-expanded', next);
    stripToggle.setAttribute('aria-expanded', next ? 'true' : 'false');
    syncStripHeight();
  });

  /* ── Strip height → CSS var so body-padding matches ──────── */
  function syncStripHeight () {
    if (!stripEl) return;
    requestAnimationFrame(() => {
      stripEl.style.setProperty('--ggw-strip-h', stripEl.offsetHeight + 'px');
    });
  }

  /* ── Header offset: place card/strip below the site header.
     Snapshot the header's natural bottom edge and expose it as a CSS custom
     property. Do NOT drive this from a scroll handler on a fixed/sticky header:
     applying the offset shifts the header, the next scroll tick re-measures the
     shifted value and it grows — that was the white-gap-above-header bug. */
  if (CONFIG.headerOffset) {
    const HEADER_SELECTOR = CONFIG.headerSelector;

    function measureHeader () {
      let bottom = 0;
      try {
        const h = document.querySelector(HEADER_SELECTOR);
        if (h) {
          // Always use the header's CURRENT bottom edge in viewport
          // coords. For fixed/sticky headers this is a constant; for
          // static headers scrolled to top it's also the right value
          // (they're visible at the top). If scrolled past a static
          // header, rect.bottom goes negative — clamp to 0 so the
          // card/strip falls back to viewport top.
          const r = h.getBoundingClientRect();
          bottom = Math.max(0, r.bottom);
        }
      } catch (_) {}
      document.documentElement.style.setProperty('--ggw-header-bottom', bottom + 'px');
    }
    // Initial measure after a tick so deferred theme JS (header
    // hydration, sticky scripts) has settled.
    setTimeout(measureHeader, 200);
    // Re-measure on resize (mobile and desktop headers differ in height),
    // rAF-throttled so iOS rubber-band scrolling cannot churn it.
    let _measureT;
    function scheduleMeasure () {
      if (_measureT) return;
      _measureT = requestAnimationFrame(() => {
        _measureT = null;
        measureHeader();
      });
    }
    window.addEventListener('resize', scheduleMeasure);

    // Re-measuring on scroll calls getBoundingClientRect() once per frame, which
    // forces a synchronous layout for the whole page on every scroll frame. Only
    // a header in normal flow can move relative to the viewport as you scroll; a
    // fixed or sticky one keeps a constant bottom edge, so skip it there.
    try {
      const h = document.querySelector(HEADER_SELECTOR);
      const pos = h && getComputedStyle(h).position;
      if (pos !== 'fixed' && pos !== 'sticky') {
        window.addEventListener('scroll', scheduleMeasure, { passive: true });
      }
    } catch (_) {
      window.addEventListener('scroll', scheduleMeasure, { passive: true });
    }
  }

  /* ── Reading the cart ────────────────────────────────────
     Counting qualifying units means knowing which cart products are in the
     bundle collection. Liquid can answer that directly, so instead of reading
     /cart.js and testing against a downloaded id set, re-render this section
     through the Section Rendering API and read the number it reports. One
     request per cart change, and none on page load. */
  async function readPaintQty () {
    const url = '/?sections=' + encodeURIComponent(CONFIG.sectionType);
    const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!res.ok) throw new Error('section render ' + res.status);
    const data = await res.json();
    const html = data[CONFIG.sectionType] || '';
    const m = html.match(/data-paint-qty="(\d+)"/);
    if (!m) throw new Error('paint qty marker missing from section render');
    return Number(m[1]);
  }

  function activeTier (q) {
    let t = null;
    for (const tier of CONFIG.tiers) if (q >= tier.qty) t = tier;
    return t;
  }
  function nextTier (q) {
    return CONFIG.tiers.find(t => t.qty > q) || null;
  }
  function barFillPct (q) {
    const t = CONFIG.tiers;
    const anchors = [
      { qty: 0,       pos: 0   },
      { qty: t[0].qty, pos: 33 },
      { qty: t[1].qty, pos: 75 },
      { qty: t[2].qty, pos: 100 },
    ];
    if (q >= t[2].qty) return 100;
    for (let i = 0; i < anchors.length - 1; i++) {
      const from = anchors[i], to = anchors[i + 1];
      if (q >= from.qty && q < to.qty) {
        const span = to.qty - from.qty;
        return Math.round(from.pos + ((q - from.qty) / span) * (to.pos - from.pos));
      }
    }
    return 0;
  }
  async function applyDiscount (code) {
    if (code === lastAppliedCode) return;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code || '' }),
      });
      lastAppliedCode = code;
    } catch (e) {
      console.warn('[GG Paint Widget] Discount apply failed:', e);
    }
  }

  /* ── Render: CARD ──────────────────────────────────────── */
  const CARD_PIPS = [33, 75, 100];

  function renderCard (qty, active, next, fillPct) {
    if (!cardEl) return;
    const noun = CONFIG.itemNoun;

    if (active) {
      cardBannerTx.innerHTML = `<strong>${active.pct}% off ${noun}s</strong> applied to your cart!`;
      cardBanner.classList.add('gg-visible');
      if (!lastActiveTier || lastActiveTier.qty !== active.qty) {
        cardBanner.classList.remove('gg-pulse');
        void cardBanner.offsetWidth;
        cardBanner.classList.add('gg-pulse');
      }
    } else {
      cardBanner.classList.remove('gg-visible');
    }

    if (next) {
      const need = next.qty - qty;
      cardMsg.classList.remove('gg-maxed');
      cardMsg.innerHTML = `Add <span class="gg-hi">${need} more ${noun}${need !== 1 ? 's' : ''}</span> to unlock <strong>${next.pct}% off</strong> all ${noun}s in your order.`;
    } else {
      cardMsg.classList.add('gg-maxed');
      cardMsg.innerHTML = `Max discount unlocked — <em>${active.pct}% off</em> all your ${noun}s. Nice one.`;
    }

    cardBarFill.style.width = fillPct + '%';
    cardBarFill.classList.toggle('gg-has-fill', fillPct > 0);
    cardBarTrack.setAttribute('aria-valuenow', fillPct);

    cardPips.innerHTML = CONFIG.tiers.map((t, i) => {
      const unlocked = qty >= t.qty;
      const isLast   = i === CONFIG.tiers.length - 1;
      const labelPos = isLast ? 'right:0;left:auto;' : `left:${CARD_PIPS[i]}%;`;
      return `
        <div class="gg-ball ${unlocked ? 'gg-ball-unlocked' : ''}" style="left:${CARD_PIPS[i]}%">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <div class="gg-ball-label ${unlocked ? 'gg-ball-label-unlocked' : ''} ${isLast ? 'gg-ball-label-end' : ''}" style="${labelPos}">${t.pct}%</div>`;
    }).join('');

    cardChip.textContent = qty + ' ' + noun.toUpperCase() + (qty !== 1 ? 'S' : '');

    // Tier badge — always show when widget is collapsed (shows "0%"
    // before any tier hits so customers know we're tracking).
    cardTierBadge.textContent = (active ? active.pct : 0) + '%';
    cardEl.classList.add('gg-has-tier');
  }

  /* ── Render: STRIP ─────────────────────────────────────── */
  const STRIP_PIPS = [33, 75, 100];

  function renderStrip (qty, active, next, fillPct) {
    if (!stripEl) return;
    const noun = CONFIG.itemNoun;

    stripCount.textContent = qty + ' ' + noun.toUpperCase() + (qty !== 1 ? 'S' : '');

    if (next) {
      const gap = next.pct - (active ? active.pct : 0);
      stripStatus.textContent = '+' + gap + '% more at ' + next.qty;
      stripStatus.classList.toggle('gg-on', !!active);
    } else {
      stripStatus.textContent = '🎉 ' + active.pct + '% off applied';
      stripStatus.classList.add('gg-on');
    }
    if (active && (!lastActiveTier || lastActiveTier.qty !== active.qty)) {
      stripStatus.classList.remove('gg-pulse');
      void stripStatus.offsetWidth;
      stripStatus.classList.add('gg-pulse');
    }

    stripFill.style.width = fillPct + '%';
    stripFill.classList.toggle('gg-has-fill', fillPct > 0);

    stripPips.innerHTML = CONFIG.tiers.map((t, i) => {
      const unlocked = qty >= t.qty;
      return `
        <div class="gg-strip-pip ${unlocked ? 'gg-unlocked' : ''}" style="left:${STRIP_PIPS[i]}%">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>`;
    }).join('');

    stripDetail.innerHTML = CONFIG.tiers.map(t => {
      const unlocked = qty >= t.qty;
      const isCurrent = active && active.qty === t.qty;
      return `
        <div class="gg-detail-cell ${unlocked ? 'gg-unlocked' : ''} ${isCurrent ? 'gg-current' : ''}">
          <div class="gg-detail-pct">${t.pct}%</div>
          <div class="gg-detail-qty">${t.qty}+ ${noun}s</div>
          <div class="gg-detail-current" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
        </div>`;
    }).join('');

    syncStripHeight();
  }

  /* ── Main tick ──────────────────────────────────────────── */
  async function tick () {
    try {
      paintQty = await readPaintQty();
      lastRead = Date.now();
      render(paintQty);
    } catch (e) {
      console.warn('[GG Paint Widget] Tick failed:', e);
    }
  }

  function render (qty) {
    if (qty === 0) {
      if (cardEl) cardEl.classList.add('gg-hidden');
      if (stripEl) {
        stripEl.classList.remove('gg-visible');
        stripEl.style.setProperty('--ggw-strip-h', '0px');
      }
      applyDiscount(null);
      lastPaintQty = 0;
      return;
    }

    // Show whichever DOMs exist; CSS gates which is actually visible.
    if (cardEl) {
      cardEl.classList.remove('gg-hidden');
      cardEl.classList.toggle('gg-collapsed', isCardCollapsed());
    }
    if (stripEl) {
      stripEl.classList.add('gg-visible');
      stripEl.classList.toggle('gg-expanded', isStripExpanded());
      syncStripHeight();
    }

    if (qty !== lastPaintQty) {
      const active = activeTier(qty);
      const next   = nextTier(qty);
      const fill   = barFillPct(qty);
      applyDiscount(active ? active.code : null);
      renderCard(qty, active, next, fill);
      renderStrip(qty, active, next, fill);
      lastActiveTier = active;
      lastPaintQty = qty;
    }
  }

  /* ── Cart change hooks ──────────────────────────────────────
     The widget is driven entirely by cart mutations — there is no polling.
     Three sources, because no single one catches everything:

       1. The theme's own events. assets/product-form.js fires on:cart:add and
          assets/cart-items.js fires on:line-item:change, both bubbling, so
          document-level listeners see adds, quantity edits and removals.
       2. Network interception, for the apps. Globo Preorder, Wishlist King and
          the quick-add drawer write to the cart without firing theme events, so
          match cart-mutating URLs on both fetch and XMLHttpRequest. Note
          /cart/update is deliberately NOT matched: applyDiscount() posts there,
          and watching it would make the widget retrigger itself forever.
       3. The tab becoming visible or being restored from bfcache, which covers
          the cart having been changed in another tab or on another page.
  ─────────────────────────────────────────────────────────── */
  const CART_MUTATION_RE = /\/cart\/(add|change|clear)/;

  // Several hooks commonly fire for one user action (add to cart, then the
  // drawer re-rendering). Coalesce them into a single /cart.js read, and never
  // run two ticks at once.
  let tickQueued = false;
  let tickInFlight = null;
  function scheduleTick () {
    if (tickQueued) return;
    tickQueued = true;
    setTimeout(() => {
      tickQueued = false;
      tickInFlight = (tickInFlight || Promise.resolve())
        .then(tick)
        .catch(() => {})
        .then(() => { tickInFlight = null; });
    }, 50);
  }

  const _origFetch = window.fetch.bind(window);
  window.fetch = function (...args) {
    const result = _origFetch(...args);
    // fetch accepts a string, a Request (.url) or a URL (.href).
    const target = args[0];
    const url = (typeof target === 'string')
      ? target
      : (target && (target.url || target.href)) || '';
    // The cart is already updated server-side once this resolves, so read it
    // straight away rather than guessing at a delay.
    if (CART_MUTATION_RE.test(url)) result.then(scheduleTick, () => {});
    return result;
  };

  const _origXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    try {
      if (typeof url === 'string' && CART_MUTATION_RE.test(url)) {
        this.addEventListener('loadend', scheduleTick);
      }
    } catch (_) {}
    return _origXhrOpen.call(this, method, url, ...rest);
  };

  document.addEventListener('on:cart:add', scheduleTick);
  document.addEventListener('on:line-item:change', scheduleTick);
  // Names other Shopify themes and apps use; harmless if never fired here.
  document.addEventListener('cart:updated', scheduleTick);
  document.addEventListener('cart:refresh', scheduleTick);

  // Re-focusing a tab is frequent on mobile and each tick is a section render,
  // so only re-read when the cart could actually have changed elsewhere.
  let lastRead = Date.now();
  const STALE_AFTER = 60000;
  document.addEventListener('visibilitychange', () => {
    if (document.hidden || (Date.now() - lastRead) < STALE_AFTER) return;
    lastRead = Date.now();
    scheduleTick();
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) scheduleTick();
  });

  /* ── Boot ───────────────────────────────────────────────── */
  // Liquid rendered the count into the page, so the first paint needs no
  // network and no idle wait.
  render(paintQty);
})();
